import type { RemarkTag } from '@app/shared/types/sf-data'

import { storeToRefs } from 'pinia'
import type { ComputedRef } from 'vue'
import { computed, unref } from 'vue'

import { stringToQuote } from '@app/utils/string-helpers'

import useRemarkTags from './use-remark-tags'
import { useWorfklowReviewItems } from '@app/application/workflows/screens/workflow-review/store/pinia/workflow-review-items'

export default function useOpenRemarkModal({
  ledgerId,
  ledgerLabel,
  ownerId,
  ownerType,
  hidePeriodSelector,
  selectedTagIds,
  remarkText,
  modalSize,
  isClientMeetingRemark,
}: {
  ledgerId?: ComputedRef<string | number | undefined> | string | number
  ledgerLabel?: ComputedRef<string | undefined> | string
  ownerId?: ComputedRef<string | number | undefined> | string | number
  ownerType?: ComputedRef<string | undefined> | string
  hidePeriodSelector?: ComputedRef<boolean | undefined> | boolean
  selectedTagIds?: ComputedRef<number[]>
  remarkText?: ComputedRef<boolean | undefined> | boolean
  remarkTags?: ComputedRef<RemarkTag[]>
  modalSize?: ComputedRef<string | undefined> | string
  isClientMeetingRemark?: ComputedRef<boolean> | boolean
}): <P extends Record<string, unknown>, C extends Record<string, unknown>>(
  payload?: P,
  callbackPayload?: C
) => void {
  const workflowReviewItemsStore = useWorfklowReviewItems()
  const { currentReviewItem } = storeToRefs(workflowReviewItemsStore)

  const remarkTags = useRemarkTags() as ComputedRef<RemarkTag[]>

  const effectiveOwnerType = computed(() => {
    return unref(ownerType) || currentReviewItem.value?.itemClass
  })

  const effectiveOwnerId = computed(() => {
    return unref(ownerId) || currentReviewItem.value?.id
  })

  const openModal = <
    P extends Record<string, unknown>,
    C extends Record<string, unknown>
  >(
    payload: P = {} as P,
    callbackPayload: C = {} as C
  ): void => {
    const selectedText = stringToQuote(window.getSelection()?.toString() ?? '')
    const text = unref(remarkText) ? payload.text : null

    const context = {
      ownerType: effectiveOwnerType.value,
      ownerId: effectiveOwnerId.value,
      ledgerId: unref(ledgerId),
      ledgerLabel: unref(ledgerLabel),
      modalSize: unref(modalSize),
      text: text || selectedText,
      allRemarkTags: remarkTags.value,
      showTags: true,
      hidePeriodSelector: unref(hidePeriodSelector),
      selectedTagIds: selectedTagIds ? selectedTagIds.value : undefined,
      callbackPayload: callbackPayload,
      isClientMeetingRemark: unref(isClientMeetingRemark),
    }

    window.modalCitizen.openModal('new-remark', {
      ...context,
      ...payload,
    })
  }

  return openModal
}
